import { Input } from '@/shared/ui/forms/inputs/input'

import { forwardRef, useCallback, useEffect, useRef } from 'react'
import { Search as IconSearch } from 'react-bootstrap-icons'
import { useTranslation } from 'react-i18next'

import { useSelectStateContext } from '../SelectStateContext'

import { useSearchContext } from './SearchContext'
import type { SearchInputProps } from './types'

/**
 * SearchInput component - Champ de recherche avec icône pour le Select
 *
 * Composant de base pour la recherche dans un Select.
 * Utilise le composant Input avec une icône de recherche.
 * Récupère ses valeurs via le contexte Select.
 * Gère le focus automatiquement via ref.
 *
 * @example
 * ```tsx
 * <SearchInput placeholder="Rechercher..." />
 * ```
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ placeholder }, forwardedRef) => {
    const { size } = useSelectStateContext()
    const { searchValue, handleSearchChange } = useSearchContext()
    const internalRef = useRef<HTMLInputElement>(null)
    const inputRef = (forwardedRef as React.RefObject<HTMLInputElement>) || internalRef

    const { t } = useTranslation()

    useEffect(() => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus()
          }
        }, 100) // Needed for SingleSelect when opening the dropdown
      }
    }, [inputRef])

    const onChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSearchChange(e.target.value)
      },
      [handleSearchChange],
    )

    return (
      <div
        className="selectw-search"
        onKeyDown={e => {
          // Empêche la fermeture du dropdown sur Escape
          if (e.key === 'Escape') {
            e.stopPropagation()
          }
        }}
        onClick={e => {
          // Empêche la fermeture du dropdown au clic
          e.stopPropagation()
        }}
      >
        <Input
          ref={inputRef}
          type="search"
          defaultValue={searchValue}
          onChange={onChange}
          placeholder={placeholder ?? t('common.select.searchPlaceholder')}
          size={size}
        >
          <Input.Icon position="right">
            <IconSearch />
          </Input.Icon>
        </Input>
      </div>
    )
  },
)

SearchInput.displayName = 'SearchInput'
